import React from 'react';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Box, createTheme, ThemeProvider } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';

const datePickerTheme = createTheme({
  palette: {
    primary: {
      main: '#9C27B0',
      light: '#F3E5F5',
      dark: '#7B1FA2',
    },
  },
  components: {
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#9C27B0',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#7B1FA2',
            },
          },
          '&:hover': {
            backgroundColor: '#F3E5F5',
          },
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        switchViewButton: {
          color: '#9C27B0',
          '&:hover': {
            backgroundColor: '#F3E5F5',
          },
        },
        label: {
          color: '#424242',
          fontWeight: 600,
        },
      },
    },
    MuiPickersArrowSwitcher: {
      styleOverrides: {
        button: {
          color: '#9C27B0',
          '&:hover': {
            backgroundColor: '#F3E5F5',
          },
        },
      },
    },
  },
});

const StyledBox = styled(Box)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '& fieldset': {
      borderColor: '#E0E0E0',
    },
    '&:hover fieldset': {
      borderColor: '#9C27B0',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#9C27B0',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
  },
});

const DatePicker = ({
  value,
  onChange,
  variant = "single", // single, range
  placeholder = "Select date",
  label,
  disabled = false,
  readOnly = false,
  format = "MM/DD/YYYY",
  style,
  ...props
}) => {
  if (variant === "range") {
    // Simple range picker using two date pickers
    const handleStartDateChange = (newDate) => {
      const currentRange = value || { start: null, end: null };
      const newRange = { ...currentRange, start: newDate };
      if (onChange) onChange(newRange);
    };

    const handleEndDateChange = (newDate) => {
      const currentRange = value || { start: null, end: null };
      const newRange = { ...currentRange, end: newDate };
      if (onChange) onChange(newRange);
    };

    return (
      <ThemeProvider theme={datePickerTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', gap: 2, ...style }}>
            <StyledBox>
              <MuiDatePicker
                value={value?.start || null}
                onChange={handleStartDateChange}
                label="From"
                format={format}
                disabled={disabled}
                readOnly={readOnly}
                slotProps={{
                  textField: {
                    placeholder: "Start date",
                    ...props
                  }
                }}
              />
            </StyledBox>
            <StyledBox>
              <MuiDatePicker
                value={value?.end || null}
                onChange={handleEndDateChange}
                label="To"
                format={format}
                disabled={disabled}
                readOnly={readOnly}
                minDate={value?.start || undefined}
                slotProps={{
                  textField: {
                    placeholder: "End date",
                    ...props
                  }
                }}
              />
            </StyledBox>
          </Box>
        </LocalizationProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={datePickerTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <StyledBox style={style}>
          <MuiDatePicker
            value={value}
            onChange={onChange}
            label={label}
            format={format}
            disabled={disabled}
            readOnly={readOnly}
            slotProps={{
              textField: {
                placeholder,
                fullWidth: true,
                ...props
              }
            }}
          />
        </StyledBox>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default DatePicker;
